/**
 * Owner: Masa Bayu
 * File: QxCategoryBrowser.tsx
 * Date:Time: 2026-10-19 12:20
 * Description: §405 — pelayar kategori QX dua aras (/categories). Senarai penuh 35
 *   kategori utama taksonomi QX; subkategori boleh dikembangkan (peraturan 4).
 *   Setiap tindakan wired ke /all-books?category=…&subcategory=….
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  isQxMainCategoryId,
  qxCategoryName,
  qxSubcategoriesOf,
  type QxMainCategoryId,
} from '@qx/qxread-core';
import { LandingIcon } from './landing/LandingIcons';
import { CATEGORY_ICON } from './landing/landing-shared';
import { getMarketplaceLocaleProfile, resolveMarketplaceLabels } from './marketplace-locale';
import {
  listMarketplaceBooks,
  MARKETPLACE_CATEGORY_ORDER,
  type MarketplaceBook,
} from './marketplace-projection';
import './qx-category-browser.css';

export interface QxCategoryBrowserProps {
  readonly onNavigate: (path: string) => void;
  /** /categories/<id> — kategori fokus dibuka dan digulir ke. */
  readonly focusCategory?: string | undefined;
}

function countMainBooks(books: readonly MarketplaceBook[], id: QxMainCategoryId): number {
  return books.filter((book) => book.categoryId === id || book.secondaryCategoryIds.includes(id))
    .length;
}

function countSubBooks(books: readonly MarketplaceBook[], subId: string): number {
  return books.filter((book) => book.subcategoryId === subId).length;
}

export function QxCategoryBrowser({
  onNavigate,
  focusCategory,
}: QxCategoryBrowserProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => resolveMarketplaceLabels(profile), [profile]);
  const books = useMemo(() => listMarketplaceBooks(), []);
  const focused =
    focusCategory !== undefined && isQxMainCategoryId(focusCategory) ? focusCategory : null;
  const [expandedId, setExpandedId] = useState<string | null>(focused);

  return (
    <div className="qxcat">
      <header className="qxcat__head">
        <h1 className="qxcat__title">{t.categoryBrowser.heading}</h1>
        <p className="qxcat__intro">{t.categoryBrowser.intro}</p>
      </header>
      <ul className="qxcat__list" role="list" aria-label={t.categoryBrowser.heading}>
        {MARKETPLACE_CATEGORY_ORDER.map((id) => {
          const subcategories = qxSubcategoriesOf(id);
          const expanded = expandedId === id;
          const mainCount = countMainBooks(books, id);
          return (
            <li key={id} className="qxcat__item" data-expanded={expanded}>
              <div className="qxcat__row">
                <button
                  type="button"
                  className="qxcat__toggle"
                  aria-expanded={expanded}
                  aria-label={`${t.categoryBrowser.expandAria}: ${qxCategoryName(id, t.language)}`}
                  onClick={() => {
                    setExpandedId(expanded ? null : id);
                  }}
                >
                  <span className="qxcat__icon" aria-hidden="true">
                    <LandingIcon name={CATEGORY_ICON[id]} size={16} />
                  </span>
                  <span className="qxcat__name">{qxCategoryName(id, t.language)}</span>
                  <span className="qxcat__count">{mainCount}</span>
                  <span className="qxcat__chevron" aria-hidden="true">
                    <LandingIcon name="chevronRight" size={14} />
                  </span>
                </button>
                <button
                  type="button"
                  className="qxcat__browse"
                  onClick={() => {
                    onNavigate(`/all-books?category=${id}`);
                  }}
                >
                  {t.categoryBrowser.browseAll}
                </button>
              </div>
              {expanded && subcategories.length > 0 && (
                <div className="qxcat__subs">
                  <p className="qxcat__subs-title">{t.categoryBrowser.subHeading}</p>
                  <ul className="qxcat__subgrid" role="list" aria-label={`${qxCategoryName(id, t.language)} — ${t.categoryBrowser.subHeading}`}>
                    {subcategories.map((sub) => (
                      <li key={sub.id}>
                        <button
                          type="button"
                          className="qxcat__sub"
                          onClick={() => {
                            onNavigate(`/all-books?category=${id}&subcategory=${sub.id}`);
                          }}
                        >
                          <span className="qxcat__sub-name">{qxCategoryName(sub.id, t.language)}</span>
                          <span className="qxcat__sub-count">{countSubBooks(books, sub.id)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
